import { useEffect, useMemo, useRef } from 'react';
import { normalizeMappingProjectKey } from '../../../shared/lib/mappingProjectIdentity';
import { useRevitParametersStore } from '../../../shared/stores/useRevitParametersStore';

interface StartupParameterLike {
  name: string;
  parameterType?: string;
  isInstance?: boolean;
  group?: string;
  dataType?: string;
  category?: string;
  isReadOnly?: boolean;
}

export interface QuickFillAvailableParameter {
  name: string;
  dataType: string;
  category: string;
  isReadOnly: boolean;
  isInstance?: boolean;
  parameterType?: string;
  group?: string;
}

interface UseQuickFillCompleteCatalogInput {
  open: boolean;
  startupParameters: StartupParameterLike[];
  projectKey?: string | null;
  modelVersion?: number | null;
  mutationActive?: boolean;
  onWarning: (message: string) => void;
}

export function mergeQuickFillParameterCatalogs(
  startupParameters: StartupParameterLike[],
  completeParameters: StartupParameterLike[]
): QuickFillAvailableParameter[] {
  const merged = new Map<string, QuickFillAvailableParameter>();
  const add = (parameter: StartupParameterLike) => {
    const isInstance = parameter.isInstance === true || parameter.parameterType === 'Instance';
    merged.set(`${parameter.name.trim().toLowerCase()}::${isInstance ? 'instance' : 'type'}`, {
      ...parameter,
      dataType: parameter.dataType ?? '',
      category: parameter.category ?? parameter.group ?? '',
      isReadOnly: parameter.isReadOnly ?? false,
      isInstance,
    });
  };

  startupParameters.forEach(add);
  completeParameters.forEach(add);
  return Array.from(merged.values());
}

export function useQuickFillCompleteCatalog({
  open,
  startupParameters,
  projectKey,
  modelVersion,
  mutationActive = false,
  onWarning,
}: UseQuickFillCompleteCatalogInput): QuickFillAvailableParameter[] {
  const completeParameters = useRevitParametersStore((state) => state.parameters);
  const completeProjectKey = useRevitParametersStore((state) => state.projectKey);
  const completeModelVersion = useRevitParametersStore((state) => state.modelVersion);
  const completeError = useRevitParametersStore((state) => state.error);
  const fetchCompleteParameters = useRevitParametersStore((state) => state.fetchParameters);
  const lastWarningRef = useRef<string | null>(null);
  const requestedProjectKey = normalizeMappingProjectKey(projectKey);
  const requestedModelVersion = typeof modelVersion === 'number' && Number.isFinite(modelVersion)
    ? modelVersion
    : null;

  useEffect(() => {
    if (!open || mutationActive) {
      return;
    }

    void fetchCompleteParameters({
      projectKey,
      modelVersion,
      force: Boolean(useRevitParametersStore.getState().error),
    });
  }, [fetchCompleteParameters, modelVersion, mutationActive, open, projectKey]);

  useEffect(() => {
    if (!open || !completeError || lastWarningRef.current === completeError) {
      return;
    }

    lastWarningRef.current = completeError;
    onWarning('The complete family parameter catalog could not be loaded. Startup columns remain available; reopen Manage Columns to retry.');
  }, [completeError, onWarning, open]);

  return useMemo(
    () => mergeQuickFillParameterCatalogs(
      startupParameters,
      completeProjectKey === requestedProjectKey && completeModelVersion === requestedModelVersion
        ? completeParameters
        : []
    ),
    [
      completeModelVersion,
      completeParameters,
      completeProjectKey,
      requestedModelVersion,
      requestedProjectKey,
      startupParameters,
    ]
  );
}
